import { useEffect, useState } from "react";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ShareToFacebook } from "@/components/admin/ShareToFacebook";
import { RichTextEditor } from "@/components/RichText";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

export function SponsorsManager() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (!site) return <p className="text-[#555555]">Loading…</p>;

  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));
  const sponsors = site.sponsors || [];
  const add = () => set("sponsors", [...sponsors, { id: crypto.randomUUID(), name: "", image: "", website: "", description: "" }]);
  const upd = (i, k, v) => set("sponsors", sponsors.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const rm = (i) => set("sponsors", sponsors.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...sponsors];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    set("sponsors", arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Partners saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sponsors-manager">
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="save-sponsors-top-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save partners"}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <label className="block text-sm font-medium text-[#333333] mb-1">Intro text</label>
        <textarea className={input} rows={2} value={site.sponsors_intro || ""} onChange={(e) => set("sponsors_intro", e.target.value)} />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-lg">Partners</h3>
          <p className="text-xs text-[#555555]">Drag the handle to reorder. Order is saved with the page.</p>
        </div>
        <button onClick={add} data-testid="add-sponsor-btn" className="inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#c41320]">
          <Plus className="h-4 w-4" /> Add partner
        </button>
      </div>

      <div className="space-y-3">
        {sponsors.map((s, i) => (
          <div
            key={s.id || i}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={() => onDrop(i)}
            data-testid={`sponsor-row-${i}`}
            className={`flex items-start gap-3 bg-white rounded-lg border p-4 transition-all ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#1E63C4] ring-2 ring-[#1E63C4]/30" : "border-[#E5E7EB]"
            } ${dragIdx === i ? "opacity-50" : ""}`}
          >
            <button
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`sponsor-drag-${i}`}
              className="mt-1 cursor-grab active:cursor-grabbing text-[#999999] hover:text-[#1E63C4]"
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <div className="flex-1 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-[#555555]">Partner {i + 1}</span>
                <button onClick={() => rm(i)} className="text-red-500" data-testid={`remove-sponsor-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <input className={input} placeholder="Name" value={s.name} onChange={(e) => upd(i, "name", e.target.value)} />
              <ImageUpload label="Logo" value={s.image} onChange={(v) => upd(i, "image", v)} testid={`sponsor-image-${i}`} />
              <input className={input} placeholder="Website URL (optional)" value={s.website} onChange={(e) => upd(i, "website", e.target.value)} />
              <textarea className={input} rows={2} placeholder="Description" value={s.description} onChange={(e) => upd(i, "description", e.target.value)} />
              <div className="border-t border-[#E5E7EB] pt-3 space-y-2">
                <p className="text-xs font-semibold text-[#555555]">More info (shown via a &quot;More info&quot; button — useful for partners without a website)</p>
                <RichTextEditor key={s.id || i} value={s.more_info_text || ""} onChange={(v) => upd(i, "more_info_text", v)} testid={`sponsor-moreinfo-text-${i}`} placeholder="More info text — type or paste formatted text" />
                {(s.more_info_images || []).map((img, ii) => (
                  <div key={ii} className="flex items-center gap-2">
                    <div className="flex-1">
                      <ImageUpload label="" value={img} onChange={(v) => upd(i, "more_info_images", (s.more_info_images || []).map((x, xi) => (xi === ii ? v : x)))} testid={`sponsor-moreinfo-image-${i}-${ii}`} />
                    </div>
                    <button onClick={() => upd(i, "more_info_images", (s.more_info_images || []).filter((_, xi) => xi !== ii))} className="text-red-500" data-testid={`remove-sponsor-moreinfo-image-${i}-${ii}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={() => upd(i, "more_info_images", [...(s.more_info_images || []), ""])} className="inline-flex items-center gap-1 text-xs text-[#1E63C4] font-semibold" data-testid={`add-sponsor-moreinfo-image-${i}`}>
                  <Plus className="h-3.5 w-3.5" /> Add image
                </button>
              </div>
              <ShareToFacebook
                image={s.image}
                defaultCaption={[s.name ? `Proud to partner with ${s.name}!` : "", s.description].filter(Boolean).join("\n\n")}
                link={s.website || ""}
                source="partner"
                testid={`fb-sponsor-${i}`}
              />
            </div>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={busy} data-testid="save-sponsors-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
        <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save partners"}
      </button>
    </div>
  );
}
